import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Image as ImageIcon, Eye } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface ProcessingLog {
  id: string;
  receipt_id: string;
  attempt_number: number;
  status: string;
  error_message: string;
  processing_duration_ms: number;
  created_at: string;
  receipts?: {
    id: string;
    image_storage_path: string;
    user_id: string;
    status: string;
  };
}

export default function SystemLogs() {
  const [logs, setLogs] = useState<ProcessingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'success' | 'error' | 'retry'>('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('processing_logs')
        .select(`
          *,
          receipts:receipt_id (
            id,
            image_storage_path,
            user_id,
            status
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReceiptImageUrl = (imagePath: string) => {
    if (!imagePath) return null;
    const { data } = supabase.storage
      .from('receipts')
      .getPublicUrl(imagePath);
    return data?.publicUrl;
  };

  const filteredLogs =
    filter === 'all' ? logs : logs.filter((log) => log.status === filter);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'retry':
        return <RefreshCw className="w-5 h-5 text-orange-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <Activity className="w-5 h-5 text-gray-900 dark:text-gray-100" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="badge-success">Success</span>;
      case 'error':
      case 'failed':
        return <span className="badge-error">Error</span>;
      case 'retry':
        return <span className="badge-warning">Retry</span>;
      case 'processing':
        return <span className="badge-info">Processing</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">System Processing Logs</h2>
          <button
            onClick={loadLogs}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Successful</p>
              <p className="text-2xl font-bold text-green-600">
                {logs.filter((l) => l.status === 'success').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Errors</p>
              <p className="text-2xl font-bold text-red-600">
                {logs.filter((l) => l.status === 'error' || l.status === 'failed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Retries</p>
              <p className="text-2xl font-bold text-orange-600">
                {logs.filter((l) => l.status === 'retry').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Total Logs</p>
              <p className="text-2xl font-bold text-blue-600">{logs.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-4">
          {['all', 'success', 'error', 'retry'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === status
                  ? 'bg-brand-primary text-white'
                  : 'bg-gray-100 text-gray-900 dark:text-gray-100 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-gray-800 dark:text-gray-200">Loading logs...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-800 dark:text-gray-200">No logs found</div>
          ) : (
            filteredLogs.map((log) => {
              const imageUrl = log.receipts?.image_storage_path ? getReceiptImageUrl(log.receipts.image_storage_path) : null;

              return (
                <div
                  key={log.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">{getStatusIcon(log.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getStatusBadge(log.status)}
                        <span className="text-xs text-gray-800 dark:text-gray-200">
                          Attempt #{log.attempt_number}
                        </span>
                        {log.processing_duration_ms && (
                          <span className="text-xs text-gray-800 dark:text-gray-200">
                            {(log.processing_duration_ms / 1000).toFixed(2)}s
                          </span>
                        )}
                        {log.receipts?.user_id && (
                          <span className="text-xs text-gray-800 dark:text-gray-200">
                            User ID: {log.receipts.user_id.substring(0, 8)}...
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-mono text-gray-900 dark:text-gray-100 mb-1">
                        Receipt ID: {log.receipt_id}
                      </p>
                      {log.error_message && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 mt-2">
                          <p className="text-sm text-red-700 dark:text-red-300">{log.error_message}</p>
                        </div>
                      )}
                      {imageUrl && (
                        <div className="mt-3">
                          <button
                            onClick={() => setSelectedImage(imageUrl)}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            View Receipt Image
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-800 dark:text-gray-200 mt-2">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg p-4">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors z-10"
            >
              <XCircle className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
            <div className="overflow-auto max-h-[calc(90vh-2rem)]">
              <img
                src={selectedImage}
                alt="Receipt"
                className="max-w-full h-auto rounded"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
